import {BookingFlowStrategy} from "./booking-flow.strategy";
import {IReactionDisposer, reaction} from "mobx";
import {
    AvailableDepartureTripModel,
    AvailableReturnTripModel,
    AvailableTripModel
} from "../../../flight-search/models/available-trip.model";
import {
    IAnalyticsFareEvent,
    IAnalyticsLowFareEvent
} from "../../models/analytics/everyMundo-analytics-flight-search.interface";
import {
    IFlightSearchControllerViewModel
} from "../../../flight-search/flight-search-controller/flight-search-controller-view-model.interface";
import {AvailabilityModel} from "../../../flight-search/models/availability.model";
import {ILowFareResult, IRoundTripLowFareReader} from "../../../low-fare/low-fare-readers/low-fare-reader.interface";
import {IServiceFactory} from "../../../service-factory.interface";
import {BookingModel} from "../../models/booking.model";

export class BookingFlowEveryMundoAnalyticsReporter {
    constructor(private readonly strategy: BookingFlowStrategy) {
        this._reactions.push(reaction(() => {
                return {
                    departureTrips: this.availability.departureTrips,
                    returnTrips: this.availability.returnTrips
                }
            },
            async (trips) => {
                await this._sendSearchAnalyticsEventData(trips);
            }, {
                fireImmediately: true
            }))
    }


    protected _reactions: IReactionDisposer[] = [];

    dispose(): void {
        this._reactions.forEach(r => r());
        this._reactions = [];
    }

    private get booking(): BookingModel {
        return this.strategy.booking;
    }
    private get services(): IServiceFactory {
        return this.booking.services;
    }
    private get flightSearchController(): IFlightSearchControllerViewModel {
        return this.strategy.flightSearchController;
    }

    private get availability(): AvailabilityModel {
        return this.strategy.availability;
    }

    private getLowFaresReader(): IRoundTripLowFareReader {
        return this.strategy.getLowFaresReader();
    }

    private async _sendSearchAnalyticsEventData(trips: {departureTrips: AvailableDepartureTripModel[], returnTrips: AvailableReturnTripModel[]}): Promise<void> {

        const {departureTrips, returnTrips} = trips;

        let lowFareResults: IAnalyticsLowFareEvent[] = [];
        let fareResults: IAnalyticsFareEvent[] = [];

        if (departureTrips && departureTrips.length > 0 && this.flightSearchController.departureDate) {
            lowFareResults = [...lowFareResults, ...await this._getLowFareForTrip(true, this.flightSearchController.departureDate)];
            fareResults = [...fareResults, ...this._getFareForTrip(true, departureTrips)];
        }
        if (returnTrips && returnTrips.length > 0 && this.flightSearchController.returnDate) {
            lowFareResults = [...lowFareResults, ...await this._getLowFareForTrip(false, this.flightSearchController.returnDate)];
            fareResults = [...fareResults, ...this._getFareForTrip(false, returnTrips)];
        }


        this.services.analytics.logFlightSearchForEveryMundo({
            BookingFlowType: 'Standard',
            CurrencyCode: this.booking.currency,
            Device: this._formatDeviceType(),
            Fare: fareResults,
            LowFare: lowFareResults,
            FlightPassengerAdult: this.flightSearchController.passengers.countAdults(),
            FlightPassengerChild: this.flightSearchController.passengers.countChildren(),
            FlightPassengerInfant: this.flightSearchController.passengers.countInfants(),
            FlightTotalPax: this.flightSearchController.passengers.getSelectedOnly().sum(item => item.count),
            Language: this.services.language.currentLanguage
        })
    }

    private async _getLowFareForTrip(isOutbound: boolean,
                                     searchDate: Date): Promise<IAnalyticsLowFareEvent[]> {
        const getLowFareFunc = this._getLowFareStrategy(isOutbound);



        const lowFareResult: IAnalyticsLowFareEvent[] = [];

        for(let i = -3; i <= 3; i++) {
            const date = this.services.time.addDays(searchDate, i);
            const price = (await getLowFareFunc(date)).price;
            if(price) {
                lowFareResult.push({
                    Date: this.services.time.formatMM_DD_YYYY_withSlash(date),
                    LowestFare: price.amount,
                    IsOutbound: isOutbound
                })
            }
        }

        return lowFareResult;
    }

    private _getLowFareStrategy(isOutbound: boolean): (date: Date) => Promise<ILowFareResult> {
        return isOutbound ?
            (date: Date) => this.getLowFaresReader().getDepartureLowFareAsync(date) :
            (date: Date) => this.getLowFaresReader().getReturnLowFareAsync(date);
    }

    private _formatDeviceType(): string {
        return this.services.device.isMobile ? "Mobile": "Desktop";
    }


    private _getFareForTrip(isOutbound: boolean, trips: AvailableTripModel[]): IAnalyticsFareEvent[] {
        return trips
            .selectMany(trip =>
                trip.journeys.map(journey => {
                    return {
                        FlightNumber: journey.segments.map(s => s.identifier.fullIdentifierCode()).join(('|')),
                        OperatingAirline: journey.segments.map(s => s.identifier.carrierCode).distinct(c => c).join('|'),
                        FareCategory: "Economy",//static value for all flights
                        FareCategoryType: journey.fares[0].productClass, //only on product class for journey
                        FlightOutboundDate: this.services.time.formatMM_DD_YYYY_withSlash(journey.designator.departureDate),
                        FlightReturnDate: this.services.time.formatMM_DD_YYYY_withSlash(journey.designator.departureDate),
                        DepartureTime: journey.designator.formatDepartureTime(),
                        ArrivalTime: journey.designator.formatArrivalTime(),
                        FlightDuration: `${Math.floor(journey.designator.travelTime.totalHours)}hrs:${journey.designator.travelTime.minutes}mins`,
                        Layover: journey.designator.numberOfStops > 0,
                        FlightOrigin: journey.designator.origin.stationCode,
                        Destination: journey.designator.destination.stationCode,
                        FlightType: this.flightSearchController.isOneWay ? "OneWay" : "RoundTrip",
                        RewardsPresent: false, // this is the default on the old app
                        FlightRouteType: journey.segments.some(s => !s.isDomesticFlight) ? "International" : "Domestic",   // add to constants
                        IsOutbound: isOutbound,
                        Cupon: null,
                        Price: journey.fares[0]?.discountedPrice.amount
                    } as IAnalyticsFareEvent;
                }));
    }

}